import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  api_create_send_message__url,
  api_get_job_messages_url,
  api_submit_job_url,
  api_get_job_detail_url,
  api_update_job_url,
  api_download_file_url,
  api_download_zip_file_url,
  api_job_list_url,
  api_send_proof,
  api_get_proof_log,
  api_viewproof,
  api_proof_approve,
  api_proof_reject,
  api_add_brand,
  api_get_sidebar_menu,
  api_create_folder,
  api_get_folders,
  api_update_folder,
  api_delete_folder,
  api_download_corrections_report,
  api_download_completed_report,
  api_add_correction_url,
  api_update_correction_url
} from "./jobApi";
import {
  parser_create_send_message,
  parser_get_job_messages,
  parser_submit_job,
  parser_get_job_detail,
  parser_update_job,
  parser_download_file,
  parser_download_zip_file,
  parser_job_list,
  parser_send_proof,
  parser_proof_log_table,
  parser_viewproof,
  parser_proof_approve,
  parser_proof_reject,
  parser_add_brand,
  parser_get_sidebar_menu,
  parser_get_folders,
  parser_create_folder,
  parser_update_folder,
  parser_delete_folder
} from "./jobParser";
import { setCommonLoading } from "../user/userSlice";
import { isAuth } from "../../helpers/cookies";

export const create_send_message = createAsyncThunk(
  "create_send_message",
  async (payload) => {
    try {
      const { job_id, action_type, message, attachment, prev_stage, new_stage } = payload;
      const formData = new FormData();
      if (typeof message === 'string') {
        formData.append("message", message);
      }
      if (attachment) {
        formData.append("attachment", attachment);
      }
      if (prev_stage) {
        formData.append("prev_stage", prev_stage);
      }
      if (new_stage) {
        formData.append("new_stage", new_stage);
      }
      const auth = isAuth();
      const auth_user = parseInt(auth?.user_id || auth?.id);
      if (auth_user) {
        formData.append("auth_user", auth_user);
      }
      const response = await api_create_send_message__url(job_id, action_type, formData);
      const data = parser_create_send_message(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);


export const get_job_messages = createAsyncThunk(
  "get_job_messages",
  async (payload) => {
    try {
      const { job_id } = payload;
      const response = await api_get_job_messages_url(job_id);
      const data = parser_get_job_messages(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const submit_job = createAsyncThunk(
  "submit_job",
  async (payload, { dispatch, rejectWithValue }) => {
    const { company_id, process_id, job_data } = payload;

    const appendFiles = (key, files, formData) => {
      if (Array.isArray(files)) {
        files
          .filter((f) => f instanceof File)
          .forEach((file) => formData.append(key, file));
      }
    };

    try {
      const formData = new FormData();

      const cleanForJson = (val) => {
        if (val instanceof File) return undefined;
        if (Array.isArray(val)) return val.map(cleanForJson).filter(v => v !== undefined);
        if (val !== null && typeof val === 'object') {
          const res = {};
          for (let k in val) {
            if (k === '__isNew') continue;
            const v = cleanForJson(val[k]);
            if (v !== undefined) res[k] = v;
          }
          return res;
        }
        return val;
      };

      if (job_data.stage_id) {
        formData.append("stage_id", job_data.stage_id);
      }
      formData.append("job_data", JSON.stringify(cleanForJson(job_data)));

      // Top-level files
      appendFiles("submitted_file_list", job_data.submitted_file, formData);

      // Job items files
      if (Array.isArray(job_data.job_items)) {
        job_data.job_items.forEach((item, index) => {
          appendFiles(`job_items[${index}][submitted_file][]`, item.submitted_file, formData);
          appendFiles(`job_items[${index}][completed_file][]`, item.completed_file, formData);
          appendFiles(`job_items[${index}][eps_file][]`, item.eps_file, formData);
          appendFiles(`job_items[${index}][logo_file][]`, item.logo_file, formData);
        });
      }

      const response = await api_submit_job_url(company_id, process_id, formData, job_data.stage_id);
      const data = parser_submit_job(response);
      return { data };

    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const get_job_detail = createAsyncThunk(
  "get_job_detail",
  async (payload, { rejectWithValue }) => {
    try {
      const { job_id } = payload;
      const response = await api_get_job_detail_url(job_id);
      const data = parser_get_job_detail(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const get_job_list = createAsyncThunk(
  "get_job_list",
  async ({ company_id, process_id, stage_id, skip = 0, limit = 10, qn, column, from_date, to_date, correction_type }) => {
    try {
      const response = await api_job_list_url(company_id, process_id, stage_id, skip, limit, qn, column, from_date, to_date, correction_type);
      return parser_job_list(response);
    } catch (error) {
      throw new Error(error);
    }
  },
  {
    condition: (payload, { getState }) => {
      const { job } = getState();
      if (job.is_job_list_loading) {
        return false;
      }
    }
  }
);

export const update_job = createAsyncThunk(
  "update_job",
  async (payload, { dispatch, rejectWithValue }) => {
    const { job_id, job_data } = payload;

    const appendFiles = (key, files, formData) => {
      if (Array.isArray(files)) {
        files
          .filter((f) => f instanceof File)
          .forEach((file) => formData.append(key, file));
      }
    };

    try {
      const formData = new FormData();

      const cleanForJson = (val) => {
        if (val instanceof File) return undefined;
        if (Array.isArray(val)) return val.map(cleanForJson).filter(v => v !== undefined);
        if (val !== null && typeof val === 'object') {
          const res = {};
          for (let k in val) {
            if (k === '__isNew') continue;
            const v = cleanForJson(val[k]);
            if (v !== undefined) res[k] = v;
          }
          return res;
        }
        return val;
      };

      formData.append("job_data", JSON.stringify(cleanForJson(job_data)));

      // Top-level files
      appendFiles("submitted_file_list", job_data.submitted_file, formData);
      appendFiles("completed_file_list", job_data.completed_file, formData);

      // Job items files
      if (Array.isArray(job_data.job_items)) {
        job_data.job_items.forEach((item, index) => {
          appendFiles(`job_items[${index}][submitted_file][]`, item.submitted_file, formData);
          appendFiles(`job_items[${index}][completed_file][]`, item.completed_file, formData);
          appendFiles(`job_items[${index}][eps_file][]`, item.eps_file, formData);
          appendFiles(`job_items[${index}][logo_file][]`, item.logo_file, formData);
        });
      }

      const response = await api_update_job_url(job_id, formData);
      const data = parser_update_job(response);
      dispatch(get_job_detail({ job_id }));
      return { data };

    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const download_file = createAsyncThunk(
  "download_file",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const { job_id, file_key, d_type } = payload;
      const response = await api_download_file_url(job_id, file_key, d_type);
      const data = parser_download_file(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  }
);

export const download_zip_file = createAsyncThunk(
  "download_zip_file",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const { job_id, comp_file_list } = payload;
      const response = await api_download_zip_file_url(job_id, comp_file_list);
      const data = parser_download_zip_file(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  }
);

export const send_proof = createAsyncThunk(
  'send proof',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const response = await api_send_proof(payload);
      const data = parser_send_proof(response)
      return { data }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  }
);

export const proof_log_table = createAsyncThunk(
  'proof log table',
  async (payload) => {
    try {
      const response = await api_get_proof_log(payload)
      const data = await parser_proof_log_table(response)
      return { data }
    } catch (error) {
      throw new Error(error)
    }
  }
)

export const get_viewproof = createAsyncThunk(
  "get_viewproof",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const { proof_id } = payload;
      const response = await api_viewproof(proof_id);
      const data = parser_viewproof(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  }
);

export const proof_approve = createAsyncThunk(
  "proof approve",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const response = await api_proof_approve(payload);
      const data = await parser_proof_approve(response);
      return { data };
    }
    catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  }
);

export const proof_reject = createAsyncThunk(
  "proof reject",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCommonLoading(true));
      const response = await api_proof_reject(payload);
      const data = await parser_proof_reject(response);
      return { data };
    }
    catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      dispatch(setCommonLoading(false));
    }
  }
);

export const add_brand = createAsyncThunk(
  "add_brand",
  async ({ job_id, prooflog_id, name, search_param, file_path, is_personal, folder_id, new_folder_name }, { rejectWithValue }) => {
    try {
      const response = await api_add_brand({ job_id, prooflog_id, name, search_param, file_path, is_personal, folder_id, new_folder_name });
      const data = await parser_add_brand(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const get_brand_library = createAsyncThunk(
  "get_brand_library",
  async ({ job_id, process_id, search, company_id, is_personal, folder_id } = {}, { rejectWithValue }) => {
    try {
      // Import here to avoid circular dependency if needed, but imported at top
      const { api_get_brand_list } = require("./jobApi");
      const { parser_get_brand_list } = require("./jobParser");

      const response = await api_get_brand_list({ job_id, process_id, search, company_id, is_personal, folder_id });
      const data = parser_get_brand_list(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const remove_brand = createAsyncThunk(
  "remove_brand",
  async (item_id, { rejectWithValue }) => {
    try {
      const { api_remove_brand } = require("./jobApi");
      const response = await api_remove_brand(item_id);
      return { item_id };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const get_sidebar_menu = createAsyncThunk(
  "get_sidebar_menu",
  async (payload, { rejectWithValue }) => {
    try {
      const { process_id, qn, column, from_date, to_date, correction_type } = typeof payload === 'object' ? payload : { process_id: payload };
      const response = await api_get_sidebar_menu(process_id, qn, column, from_date, to_date, correction_type);
      const data = parser_get_sidebar_menu(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    condition: (payload, { getState }) => {
      const { job } = getState();
      if (job.is_sidebar_loading) {
        return false;
      }
    }
  }
);

export const get_folders = createAsyncThunk(
  "get_folders",
  async ({ parent_id, is_personal, all_levels, search }, { rejectWithValue }) => {
    try {
      const response = await api_get_folders({ parent_id, is_personal, all_levels, search });
      const data = parser_get_folders(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const create_folder = createAsyncThunk(
  "create_folder",
  async ({ name, parent_id, is_personal }, { rejectWithValue }) => {
    try {
      const response = await api_create_folder({ name, parent_id, is_personal });
      const data = parser_create_folder(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const update_folder = createAsyncThunk(
  "update_folder",
  async ({ folder_id, name }, { rejectWithValue }) => {
    try {
      const response = await api_update_folder({ folder_id, name });
      const data = parser_update_folder(response);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const delete_folder = createAsyncThunk(
  "delete_folder",
  async (folder_id, { rejectWithValue }) => {
    try {
      const response = await api_delete_folder(folder_id);
      const data = parser_delete_folder(response);
      return { folder_id, data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const download_corrections_report = createAsyncThunk(
  "download_corrections_report",
  async ({ from_date, to_date }, { rejectWithValue }) => {
    try {
      const data = await api_download_corrections_report(from_date, to_date);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const download_pp_report = createAsyncThunk(
  "download_pp_report",
  async ({ from_date, to_date }, { rejectWithValue }) => {
    try {
      const data = await api_download_completed_report(from_date, to_date);
      return { data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const add_correction = createAsyncThunk(
  "add_correction",
  async (payload, { dispatch, rejectWithValue }) => {
    const { job_id, item_id, correction_data, files } = payload;
    try {
      const formData = new FormData();
      formData.append("correction_data", JSON.stringify(correction_data));

      if (files) {
        Object.keys(files).forEach((field) => {
          if (Array.isArray(files[field])) {
            files[field].forEach((f) => {
              const file = f instanceof File ? f : f?.file;
              if (file instanceof File) {
                formData.append(`${field}[]`, file);
              }
            });
          }
        });
      }

      const response = await api_add_correction_url(job_id, item_id, formData);
      dispatch(get_job_detail({ job_id }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const update_correction = createAsyncThunk(
  "update_correction",
  async (payload, { dispatch, rejectWithValue }) => {
    const { job_id, item_id, correction_id, correction_data, files } = payload;
    try {
      const formData = new FormData();
      formData.append("correction_data", JSON.stringify(correction_data));

      if (files) {
        Object.keys(files).forEach((field) => {
          if (Array.isArray(files[field])) {
            files[field].forEach((f) => {
              const file = f instanceof File ? f : f?.file;
              if (file instanceof File) {
                formData.append(`${field}[]`, file);
              }
            });
          }
        });
      }

      const response = await api_update_correction_url(job_id, item_id, correction_id, formData);
      dispatch(get_job_detail({ job_id }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  job_id: null,
  is_job_loading: false,
  send_message_loading: false,
  job_data: {},
  job_messages: [],
  job_detail: null,
  tab_index: 0,
  prev_stage: null,
  new_stage: null,
  job_list: [],
  total_job_count: 0,
  page: 0,
  search_query: '',
  search_columns: {},
  table_columns: [],
  searchFilters: [],
  search_column: "",
  send_proof: null,
  proof_data: [],
  proof_log_data: [],
  proofLoading: false,
  is_reject_modal_open: false,
  reject_comment: "",
  branded_library: [],
  sidebar_menus: [],
  job_update_flag: 0,
  correctionType: [],
  fromDate: null,
  toDate: null,
  folders: [],
  folderLoading: false,
  is_job_list_loading: false,
  is_sidebar_loading: false,
  common_loading: false,
  pending_correction_ids: [],
  pending_correction: null, // { itemIndex, description, correction_type, files }
  sending_message: null,
  is_left_sidebar_open: true
}

export const jobSlice = createSlice({
  name: "job",
  initialState,
  reducers: {
    setJobPage: (state, action) => {
      state.page = action.payload;
    },
    setJobData: (state, action) => {
      state.job_data = action.payload;
    },
    setTabIndex: (state, action) => {
      state.tab_index = action.payload;
    },
    setPrevStage: (state, action) => {
      state.prev_stage = action.payload;
    },
    setNewStage: (state, action) => {
      state.new_stage = action.payload;
    },
    clearJobData: (state) => {
      state.job_data = {};
    },
    clearJobDetail: (state) => {
      state.job_detail = null;
      state.job_id = null;
      state.proof_log_data = [];
      state.tab_index = 0;
    },
    setAction: (state, action) => {
      state.c_action = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.search_query = action.payload;
    },
    setSearchColumn: (state, action) => {
      state.search_column = action.payload;
    },
    setRejectModalOpen: (state, action) => {
      state.is_reject_modal_open = action.payload;
    },
    setRejectComment: (state, action) => {
      state.reject_comment = action.payload;
    },
    setSearchFilters: (state, action) => {
      state.searchFilters = action.payload; // array of { key, value, label }
    },
    addSearchFilter: (state, action) => {
      state.searchFilters.push(action.payload);
    },
    removeSearchFilter: (state, action) => {
      const { key, value } = action.payload;
      state.searchFilters = state.searchFilters.filter(
        f => f.key !== key || f.value !== value
      );
    },
    clearSearchFilters: (state) => {
      state.searchFilters = [];
    },
    setJobId: (state, action) => {
      state.job_id = action.payload;
    },
    setDateRange: (state, action) => {
      state.fromDate = action.payload.fromDate;
      state.toDate = action.payload.toDate;
    },
    setCorrectionType: (state, action) => {
      state.correctionType = action.payload;
    },
    addPendingCorrectionId: (state, action) => {
      if (!state.pending_correction_ids.includes(action.payload)) {
        state.pending_correction_ids.push(action.payload);
      }
    },
    clearPendingCorrectionIds: (state) => {
      state.pending_correction_ids = [];
    },
    setPendingCorrection: (state, action) => {
      state.pending_correction = action.payload;
    },
    clearPendingCorrection: (state) => {
      state.pending_correction = null;
    },
    setLeftSidebarOpen: (state, action) => {
      state.is_left_sidebar_open = action.payload;
    }
  },
  extraReducers: (builder) => {
    // create send message
    builder.addCase(create_send_message.pending, (state, action) => {
      state.send_message_loading = true;
      state.sending_message = action.meta.arg;
    })
    builder.addCase(create_send_message.fulfilled, (state, action) => {
      state.send_message_loading = false;
      state.job_messages.unshift(action.payload.data)
      state.sending_message = null;
    })
    builder.addCase(create_send_message.rejected, (state) => {
      state.send_message_loading = false;
      state.sending_message = null;
    })

    // get job send messages
    builder.addCase(get_job_messages.pending, (state) => {
      state.send_message_loading = true;
    })
    builder.addCase(get_job_messages.fulfilled, (state, action) => {
      state.send_message_loading = false;
      state.job_messages = action.payload.data
    })
    builder.addCase(get_job_messages.rejected, (state) => {
      state.send_message_loading = false;
    })

    // create job
    builder.addCase(submit_job.fulfilled, (state, action) => {
      state.job_data = action.payload.data
    })

    // get job details
    builder.addCase(get_job_detail.pending, (state) => {
      state.is_job_loading = true;
    })
    builder.addCase(get_job_detail.fulfilled, (state, action) => {
      state.is_job_loading = false;
      state.job_detail = action.payload.data
      // Note: job_id is set via setJobId from the URL param, not from job_detail response
    })
    builder.addCase(get_job_detail.rejected, (state) => {
      state.is_job_loading = false;
    })

    // update job
    builder.addCase(update_job.fulfilled, (state, action) => {
      state.job_data = action.payload.data;
      state.job_update_flag = (state.job_update_flag || 0) + 1;
    })

    // get job list 
    builder.addCase(get_job_list.pending, (state) => {
      state.common_loading = true;
      state.is_job_list_loading = true;
      state.job_list = [];
    })
    builder.addCase(get_job_list.fulfilled, (state, action) => {
      state.common_loading = false;
      state.is_job_list_loading = false;
      state.job_list = action.payload.jobs || [];
      state.total_job_count = action.payload.total_count || 0;
      if (action.payload.search_columns) {
        state.search_columns = action.payload.search_columns;
      }
      if (action.payload.table_columns) {
        state.table_columns = action.payload.table_columns;
      }
    })
    builder.addCase(get_job_list.rejected, (state) => {
      state.common_loading = false;
      state.is_job_list_loading = false;
      state.job_list = [];
    })
    // send proof
    builder.addCase(send_proof.fulfilled, (state, action) => {
      state.send_proof = action.payload.data
    })
    // view proof
    builder.addCase(get_viewproof.pending, (state) => {
      state.proofLoading = true;
    })
    builder.addCase(get_viewproof.fulfilled, (state, action) => {
      state.proof_data = action.payload.data
      state.proofLoading = false;
    })
    builder.addCase(get_viewproof.rejected, (state) => {
      state.proofLoading = false;
    });
    // proof log
    builder.addCase(proof_log_table.fulfilled, (state, action) => {
      state.proof_log_data = action.payload.data
    })
    // proof approve
    builder.addCase(proof_approve.fulfilled, (state, action) => {
      state.proof_approve = action.payload.data
    })
    // proof rejected
    builder.addCase(proof_reject.fulfilled, (state, action) => {
      state.proof_reject = action.payload.data
    })
    // add brand 
    builder.addCase(add_brand.fulfilled, (state, action) => {
      state.loading = false;
      state.branded_library.push(action.payload.data)
    })
    // get brand list
    builder.addCase(get_brand_library.pending, (state) => {
      state.loading = true;
    })
    builder.addCase(get_brand_library.fulfilled, (state, action) => {
      state.loading = false;
      state.branded_library = action.payload.data;
    })
    builder.addCase(get_brand_library.rejected, (state) => {
      state.loading = false;
    })
    // remove brand
    builder.addCase(remove_brand.fulfilled, (state, action) => {
      state.branded_library = state.branded_library.filter(
        item => item.id !== action.payload.item_id
      );
    })
    builder.addCase(get_sidebar_menu.pending, (state) => {
      state.is_sidebar_loading = true;
    })
    builder.addCase(get_sidebar_menu.fulfilled, (state, action) => {
      state.is_sidebar_loading = false;
      state.sidebar_menus = action.payload.data;
    })
    builder.addCase(get_sidebar_menu.rejected, (state) => {
      state.is_sidebar_loading = false;
    })
    // folders
    builder.addCase(get_folders.pending, (state) => {
      state.folderLoading = true;
    })
    builder.addCase(get_folders.fulfilled, (state, action) => {
      state.folderLoading = false;
      state.folders = action.payload.data;
    })
    builder.addCase(get_folders.rejected, (state) => {
      state.folderLoading = false;
    })
    builder.addCase(create_folder.fulfilled, (state, action) => {
      state.folders.push(action.payload.data);
    })
    builder.addCase(update_folder.fulfilled, (state, action) => {
      const index = state.folders.findIndex(f => f.id === action.payload.data.id);
      if (index !== -1) {
        state.folders[index] = action.payload.data;
      }
    })
    builder.addCase(delete_folder.fulfilled, (state, action) => {
      state.folders = state.folders.filter(f => f.id !== action.payload.folder_id);
    })


    builder.addCase(add_correction.fulfilled, (state) => {
      state.job_update_flag = (state.job_update_flag || 0) + 1;
    })

  },
});

export const {
  setJobData,
  setTabIndex,
  setPrevStage,
  setNewStage,
  clearJobData,
  setJobPage,
  setAction,
  setSearchQuery,
  setSearchColumn,
  setRejectModalOpen,
  setRejectComment,
  setSearchFilters,
  addSearchFilter,
  removeSearchFilter,
  clearSearchFilters,
  setDateRange,
  setCorrectionType,
  setJobId,
  clearJobDetail,
  addPendingCorrectionId,
  clearPendingCorrectionIds,
  setPendingCorrection,
  clearPendingCorrection,
  setLeftSidebarOpen
} = jobSlice.actions;

export default jobSlice.reducer;